import { zodResolver } from "@hookform/resolvers/zod";
import { Button, EmptyState, HelmrMark, Icon, cx } from "@helmr/ui";
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { isApiError, type ApiError } from "../api/client";
import { logout, selectSession, startOtp, verifyOtp, type PickerEntry } from "../api/auth";
import { useServices } from "./services";
import { useSessionSnapshot } from "./permissions";
import { BranchPicker, GymPicker, readLastPick, writeLastPick, type LastPick } from "./picker";
import { strings } from "./strings";
import { Turnstile, turnstileSiteKey, type TurnstileHandle } from "./turnstile";
import s from "./signin.module.css";

const t = strings.signIn;
const RESEND_COOLDOWN_S = 30; // The API returns no cooldown on main; this is the client default (noted in the PR).

type Step = "phone" | "code" | "business" | "branch" | "staff_only";

function waitText(seconds: number | null) {
  if (!seconds) return "a few minutes";
  return seconds < 90 ? `${seconds} seconds` : `${Math.ceil(seconds / 60)} minutes`;
}

function explain(err: unknown, expired: boolean): string {
  if (!isApiError(err)) return t.errors.generic;
  const e: ApiError = err;
  if (e.isOffline) return t.errors.offline;
  if (e.status === 429 || e.code === "rate_limited") return t.errors.rateLimited(waitText(e.retryAfterSeconds));
  if (e.code === "invalid_code") return expired ? t.errors.expired : t.errors.wrong;
  if (e.code === "bot_check_failed") return t.errors.botFailed;
  if (e.isForbidden) return t.errors.noAccess;
  return e.message || t.errors.generic;
}

const phoneSchema = z.object({ phone: z.string().trim().min(6, t.errors.phone).max(20, t.errors.phone).regex(/^[+\d][\d\s()-]*$/, t.errors.phone) });
type PhoneForm = z.infer<typeof phoneSchema>;

export function SignIn() {
  const { client, session, queryClient, now } = useServices();
  const state = useSessionSnapshot();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [challenge, setChallenge] = useState<{ id: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [trust, setTrust] = useState(true);
  const [entries, setEntries] = useState<PickerEntry[]>([]);
  const [chosen, setChosen] = useState<PickerEntry | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [lastPick] = useState<LastPick | null>(() => readLastPick());
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [tick, setTick] = useState(0);
  const [siteKey] = useState(turnstileSiteKey);
  const [botToken, setBotToken] = useState<string | null>(null);
  const [botError, setBotError] = useState<string>("");
  const bot = useRef<TurnstileHandle>(null);

  useEffect(() => {
    if (cooldownUntil <= now()) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil, now]);
  void tick;

  const form = useForm<PhoneForm>({ resolver: zodResolver(phoneSchema), defaultValues: { phone: "" } });

  if (state.status === "scoped") return <Navigate to={state.returnTo ?? from ?? "/home"} replace />;

  function finish() {
    const to = session.getState().returnTo ?? from ?? "/home";
    session.clearReturnTo();
    queryClient.clear();
    navigate(to, { replace: true });
  }

  async function enter(entry: PickerEntry, branchId?: string) {
    setBusy(true);
    setPendingId(branchId ?? entry.businessId);
    setError(null);
    try {
      const res = await selectSession(client, { businessId: entry.businessId, ...(branchId ? { branchId } : {}) });
      writeLastPick({ businessId: res.businessId, ...(res.branchId ? { branchId: res.branchId } : {}) });
      session.setScoped({ token: res.token, expiresAt: res.expiresAt, businessId: res.businessId, branchId: res.branchId, staffId: res.staffId, roleId: res.roleId });
      session.setPicker(entries);
      finish();
    } catch (err) {
      if (isApiError(err) && err.code === "branch_required") {
        setChosen(entry);
        setStep("branch");
      } else {
        setError(explain(err, false));
        // The code is spent and the identity token is good: offer the pick again rather than a code that cannot work.
        setStep((s) => (s === "code" ? "business" : s));
      }
    } finally {
      setBusy(false);
      setPendingId(null);
    }
  }

  async function sendCode(values: PhoneForm) {
    if (siteKey && !botToken) { setError(botError ? `${t.errors.botCheck} (code ${botError})` : t.errors.botCheck); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await startOtp(client, { phone: values.phone, deviceId: session.deviceId(), ...(botToken ? { botToken } : {}) });
      setPhone(values.phone);
      setChallenge({ id: res.challengeId, expiresAt: res.expiresAt });
      setCooldownUntil(now() + RESEND_COOLDOWN_S * 1000);
      setStep("code");
    } catch (err) {
      if (isApiError(err) && err.status === 429 && err.retryAfterSeconds) setCooldownUntil(now() + err.retryAfterSeconds * 1000);
      setError(explain(err, false));
    } finally {
      // A Turnstile token is single-use: fetch a fresh one for a resend or a retry.
      if (siteKey) bot.current?.reset();
      setBusy(false);
    }
  }

  async function verify(code: string) {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await verifyOtp(client, { challengeId: challenge.id, code, deviceId: session.deviceId(), trustDevice: trust });
      const staff = res.businesses.filter((b) => b.staff);
      session.setIdentity({ token: res.token, expiresAt: res.expiresAt }, res.businesses);
      setEntries(res.businesses);
      if (staff.length === 0) { setStep("staff_only"); return; }
      if (staff.length === 1) { await enter(staff[0]!); return; }
      setStep("business");
    } catch (err) {
      setError(explain(err, challenge.expiresAt < now()));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    try { await logout(client); } catch { /* local sign-out is enough */ }
    session.signOut(null);
    queryClient.clear();
    setStep("phone");
    setError(null);
  }

  const staffEntries = entries.filter((e) => e.staff);
  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - now()) / 1000));

  const showBot = !!siteKey && (step === "phone" || step === "code");

  return (
    <div className={s.page}>
      <aside className={s.brand}>
        <HelmrMark tone="light" />
        <div className={s.copy}>
          <p className={s.display}>{t.brandLine.before}<span className={s.accent}>{t.brandLine.accent}</span>{t.brandLine.after}</p>
          <p className={s.lead}>{t.brandSub}</p>
        </div>
      </aside>
      <main className={s.main}>
        <div className={s.sheet}>
          {step === "phone" && (
            <form onSubmit={form.handleSubmit(sendCode)} noValidate>
              <div className={s.head}>
                <h1>{t.title}</h1>
                <p className={s.muted}>{t.intro}</p>
              </div>
              <div className={s.field}>
                <label htmlFor="phone">{t.phoneLabel}</label>
                <input id="phone" className={cx(s.input, form.formState.errors.phone && s.invalid)} type="tel" inputMode="tel" autoComplete="tel" autoFocus placeholder={t.phonePlaceholder}
                  aria-invalid={!!form.formState.errors.phone} aria-describedby={form.formState.errors.phone ? "phone-err" : undefined} {...form.register("phone")} />
                {form.formState.errors.phone ? <p id="phone-err" className={s.fieldError} role="alert">{form.formState.errors.phone.message}</p> : null}
              </div>
              {error ? <Alert>{error}</Alert> : null}
              <Button type="submit" variant="primary" className={s.cta} busy={busy} busyLabel={t.sending}>{t.sendCode}</Button>
            </form>
          )}
          {step === "code" && (
            <CodeStep
              phone={phone}
              busy={busy}
              error={error}
              trust={trust}
              onTrust={setTrust}
              secondsLeft={secondsLeft}
              onSubmit={verify}
              onResend={() => void sendCode({ phone })}
              onBack={() => { setStep("phone"); setError(null); }}
            />
          )}
          {step === "business" && (
            <GymPicker
              entries={staffEntries}
              last={lastPick}
              busy={busy}
              pendingId={pendingId}
              error={error}
              phone={phone}
              onPick={(e) => void enter(e)}
              onChangeNumber={() => { setStep("phone"); setError(null); }}
            />
          )}
          {step === "branch" && chosen && (
            <BranchPicker
              gym={chosen}
              last={lastPick}
              busy={busy}
              pendingId={pendingId}
              error={error}
              canGoBack={staffEntries.length > 1}
              onBack={() => { setChosen(null); setStep("business"); setError(null); }}
              onPick={(branchId) => void enter(chosen, branchId)}
            />
          )}
          {step === "staff_only" && (
            <EmptyState icon="users" title={t.staffOnlyTitle} description={t.staffOnlyBody} action={<Button className={s.secondary} onClick={() => void signOut()}>{t.signOut}</Button>} />
          )}
          {showBot && siteKey ? <div className={s.bot}><Turnstile ref={bot} siteKey={siteKey} onToken={setBotToken} onError={setBotError} /></div> : null}
        </div>
        {step === "phone" ? <p className={s.help}>{t.help}</p> : null}
      </main>
    </div>
  );
}

function Alert({ children }: { children: string }) {
  return <p className={s.alert} role="alert"><Icon name="alert" size={18} />{children}</p>;
}

function CodeStep(props: {
  phone: string; busy: boolean; error: string | null; trust: boolean; onTrust: (v: boolean) => void; secondsLeft: number;
  onSubmit: (code: string) => void; onResend: () => void; onBack: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [local, setLocal] = useState<string | null>(null);
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const fill = (from: number, text: string) => {
    const only = text.replace(/\D/g, "");
    setLocal(null);
    if (!text) { setDigits(digits.map((d, i) => (i === from ? "" : d))); return; }
    if (!only) return;
    const next = [...digits];
    for (let i = 0; i < only.length && from + i < 6; i++) next[from + i] = only[i]!;
    setDigits(next);
    refs.current[Math.min(from + only.length, 5)]?.focus();
    if (next.every(Boolean)) props.onSubmit(next.join(""));
  };
  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setLocal(null);
    // A pasted code fills every box, wherever the cursor was.
    const text = e.clipboardData.getData("text");
    setDigits(Array(6).fill(""));
    const only = text.replace(/\D/g, "").slice(0, 6);
    const next = Array(6).fill("") as string[];
    [...only].forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    refs.current[Math.min(only.length, 5)]?.focus();
    if (only.length === 6) props.onSubmit(only);
  };
  const message = local ?? props.error;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (digits.every(Boolean)) { setLocal(null); props.onSubmit(digits.join("")); } else setLocal(t.errors.codeFormat); }} noValidate>
      <div className={s.head}>
        <h1>{t.codeTitle}</h1>
        <p className={s.muted}>{t.codeIntro(props.phone)}</p>
      </div>
      <div className={cx(s.boxes, message && s.boxesError)} role="group" aria-label={t.codeTitle} aria-describedby={message ? "code-err" : undefined}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            className={s.box}
            data-filled={d ? "" : undefined}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            autoFocus={i === 0}
            maxLength={i === 0 ? 6 : 1}
            value={d}
            aria-label={t.codeLabel(i + 1)}
            aria-invalid={message ? true : undefined}
            onChange={(e) => fill(i, e.target.value)}
            onKeyDown={onKeyDown(i)}
            onPaste={onPaste}
            onFocus={(e) => e.currentTarget.select()}
          />
        ))}
      </div>
      {message ? <p id="code-err" className={s.alert} role="alert"><Icon name="alert" size={18} />{message}</p> : null}
      <label className={s.trust}><input type="checkbox" checked={props.trust} onChange={(e) => props.onTrust(e.target.checked)} />{t.trust}</label>
      <Button type="submit" variant="primary" className={s.cta} busy={props.busy} busyLabel={t.verifying}>{t.verify}</Button>
      <div className={s.links}>
        <button type="button" className={s.link} onClick={props.onBack}>{t.changeNumber}</button>
        <button type="button" className={s.link} disabled={props.secondsLeft > 0 || props.busy} onClick={props.onResend}>
          {props.secondsLeft > 0 ? <><Icon name="clock" size={16} />{t.resendIn(props.secondsLeft)}</> : t.resend}
        </button>
      </div>
    </form>
  );
}
